import * as React from 'react'

import { ContactSupportLink } from '@webapp/core/components/ContactSupportLink'
import { useAppRoutes, usePaymentPermissions } from '@webapp/hooks'
import { useProcurifyIntl } from '@webapp/hooks'
import {
  PaymentErrorTypes,
  type PaymentErrorTypeEnum,
} from '@webapp/modules/payments/entities/enums/PaymentErrorTypeEnum'
import messages from './messages'

export const useGetPaymentErrorMessages = () => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const { canManageLinkedBankAccounts } = usePaymentPermissions()

  return (
    type: PaymentErrorTypeEnum | undefined,
    message: string | undefined
  ): {
    header: React.ReactNode
    message: React.ReactNode
    resolutionStep: React.ReactNode
  } => {
    switch (type) {
      case PaymentErrorTypes.BALANCE_INSUFFICIENT:
        return {
          header: intl.formatMessage(messages.balanceInsufficientHeader),
          message: intl.formatMessage(messages.balanceInsufficientMessage),
          resolutionStep: intl.formatMessage(
            messages.balanceInsufficientResolutionStep
          ),
        }
      case PaymentErrorTypes.ACCOUNT_CLOSED:
        return {
          header: intl.formatMessage(messages.accountClosedHeader),
          message: intl.formatMessage(messages.accountClosedMessage),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.ACCOUNT_FROZEN:
        return {
          header: intl.formatMessage(messages.accountFrozenHeader),
          message: intl.formatMessage(messages.accountFrozenMessage),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.BANK_ACCOUNT_RESTRICTED:
        return {
          header: intl.formatMessage(messages.bankAccountRestrictedHeader),
          message: intl.formatMessage(messages.bankAccountRestrictedMessage),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.COULD_NOT_PROCESS:
        return {
          header: intl.formatMessage(messages.couldNotProcessHeader),
          message: intl.formatMessage(messages.couldNotProcessMessage),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.INVALID_ACCOUNT_NUMBER:
        return {
          header: intl.formatMessage(messages.invalidAccountNumberHeader),
          message: intl.formatMessage(messages.invalidAccountNumberMessage),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.INCORRECT_ACCOUNT_HOLDER_NAME:
        return {
          header: intl.formatMessage(messages.incorrectAccountHolderNameHeader),
          message: intl.formatMessage(
            messages.incorrectAccountHolderNameMessage
          ),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.INVALID_CURRENCY:
        return {
          header: intl.formatMessage(messages.invalidCurrencyHeader),
          message: intl.formatMessage(messages.invalidCurrencyMessage),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.INCORRECT_BANKING_INFORMATION:
        return {
          header: intl.formatMessage(
            messages.incorrectBankingInformationHeader
          ),
          message: intl.formatMessage(
            messages.incorrectBankingInformationMessage
          ),
          resolutionStep: intl.formatMessage(
            messages.genericIncorrectBankingInformationResolutionStep
          ),
        }
      case PaymentErrorTypes.REGULATORY_REASON:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.regulatoryReasonMessage),
          resolutionStep: <ContactSupportLink />,
        }
      case PaymentErrorTypes.SYNTAX_ERROR:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.syntaxErrorMessage),
          resolutionStep: <ContactSupportLink />,
        }
      case PaymentErrorTypes.INSUFFICIENT_FUNDS:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(
            messages.directDebitInsufficientFundsMessage
          ),
          resolutionStep: intl.formatMessage(
            messages.directDebitInsufficientFundsResolutionStep,
            {
              a: (text) =>
                canManageLinkedBankAccounts ? (
                  <a
                    href={`${url('SettingsManageBankAccountsPage')}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {text}
                  </a>
                ) : (
                  <>{text}</>
                ),
            }
          ),
        }
      case PaymentErrorTypes.CLOSED_ACCOUNT_NUMBER:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.closedAccountNumberMessage),
          resolutionStep: intl.formatMessage(
            messages.closedAccountNumberResolutionStep
          ),
        }
      case PaymentErrorTypes.BLOCKED_AMOUNT:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.blockedAmountMessage),
          resolutionStep: intl.formatMessage(
            messages.directDebitInsufficientFundsResolutionStep,
            {
              a: (text) =>
                canManageLinkedBankAccounts ? (
                  <a
                    href={`${url('SettingsManageBankAccountsPage')}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {text}
                  </a>
                ) : (
                  <>{text}</>
                ),
            }
          ),
        }
      case PaymentErrorTypes.BLOCKED_ACCOUNT:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.blockedAccountMessage),
          resolutionStep: intl.formatMessage(
            messages.blockedAccountResolutionStep
          ),
        }
      case PaymentErrorTypes.INVALID_FILE_FORMAT:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.invalidFileFormatMessage),
          resolutionStep: intl.formatMessage(
            messages.genericDirectDebitErrorResolutionStep
          ),
        }
      case PaymentErrorTypes.SETTLEMENT_FAILED:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.settlementFailedMessage),
          resolutionStep: intl.formatMessage(
            messages.genericDirectDebitErrorResolutionStep
          ),
        }
      case PaymentErrorTypes.LIMITED_PARTICIPATION_DFI:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(messages.limitedParticipationDFIMessage),
          resolutionStep: intl.formatMessage(
            messages.genericDirectDebitErrorResolutionStep
          ),
        }
      case PaymentErrorTypes.INACTIVE_LINKED_BANK_ACCOUNT:
        return {
          header: intl.formatMessage(messages.genericDirectDebitErrorHeader),
          message: intl.formatMessage(
            messages.inactiveLinkedBankAccountMessage
          ),
          resolutionStep: intl.formatMessage(
            messages.inactiveLinkedBankAccountResolutionStep,
            {
              a: (text) =>
                canManageLinkedBankAccounts ? (
                  <a
                    href={`${url('SettingsManageBankAccountsPage')}`}
                    target='_blank'
                    rel='noreferrer'
                  >
                    {text}
                  </a>
                ) : (
                  <>{text}</>
                ),
            }
          ),
        }
      default:
        return {
          header: intl.formatMessage(messages.genericErrorHeader),
          message: message || intl.formatMessage(messages.genericErrorMessage),
          resolutionStep: <ContactSupportLink />,
        }
    }
  }
}
